/**
 * Sources section — bottom-of-page provenance list.
 *
 * Renders every `source_url` + `verified_at` pair the page cites,
 * deduplicated by URL, sorted by label. Pairs with the page's JSON-LD
 * Dataset/MedicalWebPage schemas to give crawlers a stable list of
 * "what this page is based on".
 */

export type SourceEntry = {
  url: string;
  label: string;
  verifiedAt?: string | null;
};

type Props = {
  sources: SourceEntry[];
  locale?: string;
  /** Localised section title; caller supplies the i18n string */
  title?: string;
  /** Localised "verified" label */
  verifiedLabel?: string;
};

export function SourcesSection({
  sources,
  locale = "en",
  title = "Sources",
  verifiedLabel = "verified",
}: Props) {
  const deduped = dedupeAndSort(sources);
  if (deduped.length === 0) return null;
  return (
    <section
      aria-label={title}
      className="mt-8 border-t border-border ps-0 pe-0 pt-6"
    >
      <h2 className="text-lg font-semibold mb-3">{title}</h2>
      <ul className="space-y-2 text-sm">
        {deduped.map((source) => (
          <li key={source.url} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <a
              href={source.url}
              rel="noopener noreferrer external"
              target="_blank"
              className="font-medium hover:underline break-all"
            >
              {source.label}
            </a>
            {source.verifiedAt ? (
              <span className="text-xs text-muted-foreground">
                {verifiedLabel}{" "}
                <time dateTime={source.verifiedAt}>
                  {new Date(source.verifiedAt).toLocaleDateString(locale)}
                </time>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function dedupeAndSort(sources: SourceEntry[]): SourceEntry[] {
  const seen = new Map<string, SourceEntry>();
  for (const source of sources) {
    if (!source.url) continue;
    const existing = seen.get(source.url);
    // Prefer the entry with a verifiedAt timestamp
    if (!existing || (!existing.verifiedAt && source.verifiedAt)) {
      seen.set(source.url, source);
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    (a.label ?? a.url).localeCompare(b.label ?? b.url),
  );
}
